import React, { useState, useRef, createRef, useEffect } from "react";
import { PseudoBox, Box, Button, Stack, Flex, Menu, MenuButton, MenuList } from "@chakra-ui/core";

import Basket from "components/home/Basket";
import slugify from "utils/shared/slugify";
import useWindowSize from "hooks/useWindowSize";

const NavLink = React.forwardRef(({ name, active, targetRef, ...rest }, ref) => {
  const scrollTo = (e, ele) => {
    e.preventDefault();
    ele.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <PseudoBox
      ref={ref}
      as="button"
      borderBottom="2px"
      borderColor={active ? "brand.400" : "transparent"}
      color={active ? "gray.900" : "gray.500"}
      _hover={{ borderColor: "brand.400", color: "gray.900" }}
      _focus={{ outline: "none", color: "gray.900" }}
      px="1"
      pt="1"
      display="inline-flex"
      h="full"
      alignItems="center"
      transition="all 150ms ease-in-out"
      fontSize="md"
      lineHeight="base"
      fontWeight="medium"
      onClick={(e) => {
        scrollTo(e, targetRef.current);
      }}
      {...rest}
    >
      {name}
    </PseudoBox>
  );
});

const DropMenuItem = ({ name, active, onClose, ...rest }) => {
  const scrollToSection = (e, name) => {
    e.preventDefault();
    const ele = document.getElementById(slugify(name));
    ele.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <PseudoBox
      as={Box}
      borderLeft="2px"
      borderColor={active ? "brand.400" : "transparent"}
      color={active ? "gray.900" : "gray.500"}
      _hover={{ bg: active ? "brand.50" : "gray.100", color: "gray.900" }}
      bg={active ? "brand.50" : "white"}
      px="4"
      py="1"
      display="inline-flex"
      h="full"
      alignItems="center"
      transition="all 150ms ease-in-out"
      _focus={{ outline: "none", color: "gray.900" }}
      fontSize="md"
      lineHeight="base"
      fontWeight="medium"
      cursor="pointer"
      onClick={(e) => {
        scrollToSection(e, name);
      }}
      {...rest}
    >
      {name}
    </PseudoBox>
  );
};

const MenuBar = React.forwardRef(({ sectionsRef, foods, visibleSection }, ref) => {
  const [maxNavLinks, setMaxNavLinks] = useState(999);
  const windowSize = useWindowSize();

  const isActive = foods.findIndex(([sectionName, foods]) => sectionName === visibleSection) >= maxNavLinks;

  const menuBarContainer = useRef(null);
  const basketContainer = useRef(null);

  const getWidth = (ele) => {
    if (ele !== null) {
      const { width } = ele.getBoundingClientRect();
      return width;
    } else return 0;
  };

  const [menuBarContainerWidth, setMenuBarContainerWidth] = useState();
  const [basketContainerWidth, setBasketContainerWidth] = useState();

  const navItemsRef = useRef(foods.map(() => createRef()));
  const [navItemsWidths, setNavItemsWidths] = useState([]);
  const dropdownButtonRef = useRef(null);
  const [dropdownButtonWidth, setDropdownButtonWidth] = useState();

  const getLastVisibleItemIndex = () => {
    let remainingWidth = menuBarContainerWidth - basketContainerWidth - dropdownButtonWidth;
    let lastVisible = 0;

    for (let i = 0; i < navItemsRef.current.length; i++) {
      remainingWidth = remainingWidth - navItemsWidths[i];
      if (remainingWidth < 0) {
        lastVisible -= 1;
        break;
      }
      lastVisible += 1;
    }
    return lastVisible;
  };

  const getNavItemsWidths = () => {
    let navItemsWidthsCopy = navItemsWidths;
    for (let i = 0; i < navItemsRef.current.length; i++) {
      navItemsWidthsCopy.push(getWidth(navItemsRef.current[i].current) + 16);
    }
    setNavItemsWidths(navItemsWidthsCopy);
  };

  // Get new widths when window resizes
  useEffect(() => {
    setMenuBarContainerWidth(getWidth(menuBarContainer.current));
    setBasketContainerWidth(getWidth(basketContainer.current));
    setDropdownButtonWidth(getWidth(dropdownButtonRef.current));
    getNavItemsWidths();

    if (getWidth(menuBarContainer.current) < 750) {
      setMaxNavLinks(0);
    } else {
      setMaxNavLinks(getLastVisibleItemIndex());
    }
  }, [windowSize.width]);

  // Fires when the page has loaded and containers widths are set
  useEffect(() => {
    getNavItemsWidths();

    if (menuBarContainerWidth < 750) {
      setMaxNavLinks(0);
    } else {
      setMaxNavLinks(getLastVisibleItemIndex());
    }
  }, [menuBarContainerWidth, basketContainerWidth]);

  // Fires when the visible section changes
  useEffect(() => {
    if (menuBarContainerWidth < 750) {
      setMaxNavLinks(0);
    } else {
      setMenuBarContainerWidth(getWidth(menuBarContainer.current));
      setBasketContainerWidth(getWidth(basketContainer.current));
      setDropdownButtonWidth(getWidth(dropdownButtonRef.current));
      getNavItemsWidths();
      setMaxNavLinks(getLastVisibleItemIndex());
    }
  }, [visibleSection]);

  return (
    <>
      <Box
        bg="white"
        w="full"
        borderBottom="2px"
        borderColor="gray.100"
        left="0"
        top="0"
        position={visibleSection !== undefined ? "fixed" : "sticky"}
        zIndex="sticky"
        ref={ref}
      >
        <Box maxW="7xl" mx="auto" px={{ base: "4", sm: "6", lg: "8" }}>
          <Flex justify="space-between" align="center" h="16" flexShrink="0" maxW="full" ref={menuBarContainer}>
            {/* START LEFT FOOD MENU */}
            <Stack
              isInline
              spacing={4}
              as="nav"
              display="flex"
              justifyContent="flex-start"
              alignItems="center"
              h="full"
              flexGrow="1"
              flexShrink="0"
            >
              {foods.slice(0, maxNavLinks).map(([sectionName, foods], i) => (
                <NavLink
                  name={sectionName}
                  active={visibleSection === sectionName}
                  key={sectionName}
                  targetRef={sectionsRef[i]}
                  ref={navItemsRef.current[i]}
                />
              ))}
              <Menu autoSelect={false}>
                {({ isOpen, onClose }) => {
                  if (foods.slice(maxNavLinks).length !== 0)
                    return (
                      <React.Fragment>
                        <MenuButton
                          as={Button}
                          rightIcon="chevron-down"
                          borderBottom="2px"
                          borderColor={isActive && visibleSection ? "brand.400" : "transparent"}
                          color={isActive && visibleSection ? "gray.900" : "gray.500"}
                          _hover={{ borderColor: "brand.400", color: "gray.900" }}
                          px="1"
                          pt="1"
                          display="inline-flex"
                          h="full"
                          bg="white"
                          rounded="none"
                          alignItems="center"
                          transition="all 150ms ease-in-out"
                          _focus={{ outline: "none", color: "gray.900", bg: "white" }}
                          _active={{ bg: "white" }}
                          fontSize="md"
                          lineHeight="base"
                          fontWeight="medium"
                          ref={dropdownButtonRef}
                        >
                          {isActive && visibleSection ? visibleSection : "Plus"}
                        </MenuButton>

                        <Stack as={MenuList} spacing="0">
                          {foods.slice(maxNavLinks).map(([sectionName, foods], i) => {
                            return (
                              <DropMenuItem
                                name={sectionName}
                                onClose={onClose}
                                active={visibleSection === sectionName}
                                key={sectionName}
                              />
                            );
                          })}
                        </Stack>
                      </React.Fragment>
                    );
                }}
              </Menu>
            </Stack>
            {/* END LEFT FOOD MENU */}
            {/* START RIGHT MENU */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              h="full"
              flexShrink="0"
              zIndex="10"
              pl="2"
              ref={basketContainer}
            >
              {visibleSection && <Basket />}
            </Box>
            {/* END RIGHT MENU */}
          </Flex>
        </Box>
      </Box>
    </>
  );
});

export default MenuBar;
